import React, { Suspense, useState, useEffect, useCallback, useRef } from "react";
import { MapLocation } from "../utils/MarkerClusterManager";
import { UserReport } from "./UserReportsList";
import "../styles/animations.css";
import RestoApiService, { ReportRequest, Report, RestaurantHistoryResponse, RestaurantComparisonResponse, RestaurantChainResponse } from "../utils/RestoApiService";
import ComparisonBadges from "./ComparisonBadges";
import ReportListItem from "./ReportListItem";
import { Skeleton, SkeletonText, SkeletonReportCard, SkeletonGroup } from "./Skeleton";
import CombinedRatingsTimeline from "./CombinedRatingsTimeline";
import { lazyWithRetry } from "../utils/lazyWithRetry";
import InternationalMetaTagsManager from "./InternationalMetaTagsManager";

// Modal chunks load on first open, keeping html2canvas/qrcode etc. out of
// the initial bundle
const ReportFormModal = lazyWithRetry(() => import("./ReportFormModal"));
const BadgeModal = lazyWithRetry(() => import("./BadgeModal"));
const SubscriptionModal = lazyWithRetry(() => import("./SubscriptionModal"));
const InspectionSourceModal = lazyWithRetry(() =>
  import("./InspectionSourceModal").then((m) => ({ default: m.InspectionSourceModal }))
);
import styles from "./RestaurantDetailsSidebar.module.css";
import { ErrorIcon, EmptyStateIcon, AddReportIcon, ShareIcon, CloseIcon, InfoIcon } from "./Icon";
import { sidebarStateManager, SidebarType } from "../utils/SidebarStateManager";
import { useTranslation } from "react-i18next";
import { DateFormatter } from "../utils/DateFormatter";
import { useModalState } from "../hooks/useModalState";
import { useSidebarOpenEffect } from "../hooks/useSidebarOpenEffect";
import { generateRestoSlug, getChainPath } from "../utils/RestoSlugUtils";
import AnalyticsManager from "../utils/AnalyticsManager";

interface RestaurantDetailsContentProps {
  selectedEntry: MapLocation["entry"] | null;
  selectedRestaurantId?: string | number | null;
  initialReports?: any[];
  initialChain?: RestaurantChainResponse | null;
}

// Initialize the API service
const restoApiService = new RestoApiService();

// Map API reports to UserReport format (shared between SSR hydration and client fetch)
function mapApiReportsToUserReports(data: Report[]): UserReport[] {
  const colors = ["#10B981", "#F59E0B", "#8B5CF6", "#3B82F6", "#EC4899"];
  return data.map((report: Report, index: number) => ({
    id: report.id || `report-${index}`,
    username: report.username,
    date: report.createdAt,
    comment: report.reportText,
    rating: report.rating,
    avatarColor: colors[report.username.charCodeAt(0) % colors.length],
    imageUrl: report.imageUrl,
    confirmations: (report as any).confirmations || 0,
  }));
}

// Function to derive emoji based on fork score
const getInspectionEmoji = (entry: MapLocation["entry"] | null): string => {
  if (!entry) return "N/A";

  // Try to get the fork score
  const forks = Number(entry.inspectionForks);
  if (isNaN(forks)) {
    // If no valid fork score, return existing smiley or abbreviation
    return entry.inspectionSmiley || entry.inspectionAbbreviationNl || "N/A";
  }

  // Return emoji based on fork score (1-5), matching the SearchComponent emojis
  if (forks === 5) return "😍"; // Best rating (5)
  if (forks === 4) return "😃"; // Good rating (4)
  if (forks === 3) return "🙂"; // Average rating (3)
  if (forks === 2) return "🤮"; // Bad rating (2)
  if (forks === 1) return "💀"; // Worst rating (1)
  return "❓"; // Unknown rating
};

const RestaurantDetailsContent: React.FC<RestaurantDetailsContentProps> = ({
  selectedEntry,
  selectedRestaurantId = null,
  initialReports,
  initialChain
}) => {
  const { t, i18n } = useTranslation();
  const { modals, openModal, closeModal } = useModalState();
  const [showSubscription, setShowSubscription] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareButtons, setShowShareButtons] = useState(false);
  // The inspection history is the core value of this page — keep it visible
  // by default; users can still collapse it per restaurant.
  const [isDetailsHistoryOpen, setIsDetailsHistoryOpen] = useState(true);

  // State for reports — initialize from SSR data if available
  const [reports, setReports] = useState<UserReport[]>(
    () => initialReports?.length ? mapApiReportsToUserReports(initialReports as Report[]) : []
  );
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  
  // State for history
  const [history, setHistory] = useState<RestaurantHistoryResponse | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  
  // State for subscriber count
  const [subscriberCount, setSubscriberCount] = useState<number>(0);
  const [isLoadingSubscriberCount, setIsLoadingSubscriberCount] = useState(false);
  const [subscriberCountError, setSubscriberCountError] = useState<string | null>(null);

  // State for comparison data
  const [comparison, setComparison] = useState<RestaurantComparisonResponse | null>(null);
  const [isLoadingComparison, setIsLoadingComparison] = useState(false);

  // Chain membership ("part of a chain?") — seeded by SSR for the initial
  // restaurant, fetched client-side on selection changes. null = not in a chain.
  const [chain, setChain] = useState<RestaurantChainResponse | null>(initialChain ?? null);
  const chainFetchedForIdRef = useRef<number | null>(
    initialChain && selectedEntry ? Number(selectedEntry.id) || null : null
  );

  const getShareData = () => {
    if (!selectedEntry) return null;
    const restoSlug = generateRestoSlug(selectedEntry.name, selectedEntry.id);
    const shareUrl = `${window.location.origin}${window.location.pathname}?restoId=${restoSlug}`;
    const shareTitle = t('restaurant.shareTitle', { name: selectedEntry.name });
    const ratingEmoji = getInspectionEmoji(selectedEntry);
    const shareText = t('restaurant.shareText', {
      name: selectedEntry.name,
      rating: ratingEmoji
    });
    return { shareUrl, shareTitle, shareText };
  };

  const handleShareClick = () => {
    if (selectedEntry) {
      const data = getShareData();
      if (!data) return;

      const analytics = AnalyticsManager.getInstance();
      analytics.trackEvent('share_click', {
        restaurant_id: selectedEntry.id,
        restaurant_name: selectedEntry.name,
        share_method: typeof navigator.share === 'function' ? 'native' : 'buttons',
        event_category: 'engagement',
      });

      // Try to use the Web Share API if available (mobile)
      if (navigator.share) {
        navigator.share({
          title: data.shareTitle,
          text: data.shareText,
          url: data.shareUrl,
        }).catch((err) => {
          console.error("Error sharing:", err);
          // Fallback to showing buttons
          setShowShareButtons(prev => !prev);
        });
      } else {
        // Desktop: toggle inline share buttons
        setShowShareButtons(prev => !prev);
      }
    }
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000); // Hide "Copied!" message after 2 seconds
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        // Optionally, show an error message to the user
      });
  };

  const handleCloseClick = () => {
    const analytics = AnalyticsManager.getInstance();
    analytics.trackEvent('sidebar_close', {
      restaurant_id: selectedEntry?.id,
      restaurant_name: selectedEntry?.name,
      event_category: 'navigation',
    });
    sidebarStateManager.closeSidebar();
  };

  const handleDetailsHistoryToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const isOpen = e.currentTarget.open;
    setIsDetailsHistoryOpen(isOpen);

    if (selectedEntry && isOpen) {
      AnalyticsManager.getInstance().trackEvent('details_history_toggle', {
        restaurant_id: selectedEntry.id,
        restaurant_name: selectedEntry.name,
        event_category: 'engagement',
      });
    }
  };

  // Function to fetch reports for the selected restaurant
  const fetchReports = useCallback(async (restaurantId: number) => {
    setIsLoadingReports(true);
    setReportsError(null);

    try {
      const result = await restoApiService.fetchRestaurantReports(restaurantId);

      if (result.success && result.data) {
        setReports(mapApiReportsToUserReports(result.data));
      } else {
        setReportsError(result.error || "Failed to load reports");
      }
    } catch (error) {
      console.error("Exception in fetchReports:", error);
      setReportsError(error instanceof Error ? error.message : "An unexpected error occurred while loading reports");
    } finally {
      setIsLoadingReports(false);
    }
  }, []); // Empty deps array since restoApiService is stable
  
  // Function to fetch history for the selected restaurant
  const fetchHistory = useCallback(async (restaurantId: number) => {
    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      const result = await restoApiService.fetchRestaurantHistory(restaurantId);

      if (result.success && result.data) {
        setHistory(result.data);
      } else {
        setHistoryError(result.error || "Failed to load history");
      }
    } catch (error) {
      console.error("Exception in fetchHistory:", error);
      setHistoryError(error instanceof Error ? error.message : "An unexpected error occurred while loading history");
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);
  
  // Function to fetch subscriber count for the selected restaurant
  const fetchSubscriberCount = useCallback(async (restaurantId: number) => {
    setIsLoadingSubscriberCount(true);
    setSubscriberCountError(null);

    try {
      const result = await restoApiService.fetchSubscriberCount(restaurantId);

      if (result.success && result.data) {
        setSubscriberCount(result.data.count);
      } else {
        setSubscriberCountError(result.error || "Failed to load subscriber count");
      }
    } catch (error) {
      console.error("Exception in fetchSubscriberCount:", error);
      setSubscriberCountError(error instanceof Error ? error.message : "An unexpected error occurred while loading subscriber count");
    } finally {
      setIsLoadingSubscriberCount(false);
    }
  }, []);

  // Function to fetch comparison data for the selected restaurant
  const fetchComparison = useCallback(async (restaurantId: number) => {
    setIsLoadingComparison(true);

    try {
      const result = await restoApiService.fetchRestaurantComparison(restaurantId);

      if (result.success && result.data) {
        setComparison(result.data);
      } else {
        // Silently fail - comparison data is optional
        setComparison(null);
      }
    } catch (error) {
      console.error("Exception in fetchComparison:", error);
      setComparison(null);
    } finally {
      setIsLoadingComparison(false);
    }
  }, []);

  // Track the last fetched restaurant ID to prevent duplicate fetches
  const lastFetchedIdRef = useRef<number | null>(null);

  // Fetch reports, history, subscriber count, and comparison when a restaurant is selected
  useEffect(() => {
    // Get restaurant ID from either selectedEntry or selectedRestaurantId
    const restaurantId = selectedEntry
      ? Number(selectedEntry.id) || 0
      : Number(selectedRestaurantId) || 0;

    // Only fetch if we have a valid ID and it's different from the last fetched ID
    if (restaurantId > 0 && restaurantId !== lastFetchedIdRef.current) {
      lastFetchedIdRef.current = restaurantId;
      fetchReports(restaurantId);
      fetchHistory(restaurantId);
      fetchSubscriberCount(restaurantId);
      fetchComparison(restaurantId);
    }
  }, [selectedEntry?.id, selectedRestaurantId, fetchReports, fetchHistory, fetchSubscriberCount, fetchComparison]); // Use only the ID to prevent object reference changes

  useEffect(() => {
    setIsDetailsHistoryOpen(true);
  }, [selectedEntry?.id, selectedRestaurantId]);

  // Fetch chain membership when the selected restaurant changes (the SSR'd
  // initial restaurant is already seeded via initialChain)
  useEffect(() => {
    const restaurantId = selectedEntry
      ? Number(selectedEntry.id) || 0
      : Number(selectedRestaurantId) || 0;
    if (restaurantId <= 0 || chainFetchedForIdRef.current === restaurantId) return;
    chainFetchedForIdRef.current = restaurantId;
    setChain(null);
    restoApiService.fetchRestaurantChain(restaurantId).then((result) => {
      // Ignore stale responses after another selection
      if (chainFetchedForIdRef.current !== restaurantId) return;
      setChain(result.success && result.data ? result.data : null);
    });
  }, [selectedEntry?.id, selectedRestaurantId]);

  // Refetch reports, history, subscriber count, and comparison when sidebar opens (to get latest data)
  useSidebarOpenEffect(SidebarType.RESTAURANT_DETAILS, () => {
    const restaurantId = selectedEntry
      ? Number(selectedEntry.id) || 0
      : Number(selectedRestaurantId) || 0;

    if (restaurantId > 0) {
      fetchReports(restaurantId);
      fetchHistory(restaurantId);
      fetchSubscriberCount(restaurantId);
      fetchComparison(restaurantId);
    }
  });

  // Scroll to a specific report when clicking on timeline marker
  const scrollToReport = (reportId: string) => {
    const element = document.getElementById(`report-${reportId}`);
    element?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <>
      {selectedEntry && <InternationalMetaTagsManager selectedEntry={selectedEntry} pageType="restaurant" />}
      {/* Modals are code-split and only mounted while open — they never
          render during SSR (modal state starts closed) */}
      {selectedEntry && modals.inspectionSource && (
        <Suspense fallback={null}>
          <InspectionSourceModal
            isOpen={modals.inspectionSource}
            onClose={() => closeModal('inspectionSource')}
            restaurantId={selectedEntry.id}
          />
        </Suspense>
      )}
      {selectedEntry && modals.badge && (
        <Suspense fallback={null}>
          <BadgeModal
            isOpen={modals.badge}
            onClose={() => closeModal('badge')}
            restaurantId={selectedEntry.id}
            restaurantName={selectedEntry.name}
            inspectionForks={Number(selectedEntry.inspectionForks) || undefined}
          />
        </Suspense>
      )}
      {selectedEntry && showSubscription && (
        <Suspense fallback={null}>
          <SubscriptionModal
            isOpen={showSubscription}
            onClose={() => setShowSubscription(false)}
            restaurantName={selectedEntry.name}
            restaurantId={Number(selectedEntry.id)}
          />
        </Suspense>
      )}
      <div className="flex flex-col h-full">
        {/* Main scrollable content area */}
        <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`} id="sidebar-scroll-container">
          <div className="pl-4 pr-2 pt-4 pb-6" id="sidebar-content">
            {selectedEntry ? (
              <>
                {/* Restaurant Info Card */}
                <div className={`${styles.card} ${styles.restaurantCard} smooth-transition fade-in`}>
                  {/* Tier 1 — Identity (name + score) */}
                  <div className="relative">
                    <button
                      onClick={handleCloseClick}
                      className="absolute top-0 right-0 p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      aria-label={t('restaurant.close')}
                    >
                      <CloseIcon className="h-5 w-5" />
                    </button>
                    <div className="flex justify-between items-start pr-8">
                      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 text-left">{selectedEntry.name}</h3>
                      <div className="text-right flex-shrink-0 ml-3">
                        <div className="text-3xl leading-none">{getInspectionEmoji(selectedEntry)}</div>
                        {selectedEntry.inspectionForks && (
                          <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-0.5">{selectedEntry.inspectionForks}/5</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tier 2 — Details (where/when) */}
                  <div className="mt-4">
                    <div className="text-sm text-gray-500 dark:text-gray-300 text-left">
                      {(() => {
                        const streetMatch = selectedEntry.address?.match(/^(.+?)\s*,?\s*\d{4}/);
                        const street = streetMatch?.[1]?.trim() || selectedEntry.address;
                        const cityParts = selectedEntry.address?.match(/\d{4}\s+(.+?)(?:\s+Belgi[që]ue)?$/i);
                        const city = cityParts?.[1]?.split(/[-/]/)[0]?.trim();
                        const lang = i18n.language || 'en';
                        const cityPathPrefix = lang === 'fr' ? 'ville' : lang === 'nl' ? 'stad' : 'city';
                        const citySlug = city?.toLowerCase().replace(/\s+/g, '-');
                        return (
                          <>
                            <span>{street}</span>
                            {city && (
                              <>
                                <span className="mx-1.5 text-gray-400">·</span>
                                <a
                                  href={`/${lang}/${cityPathPrefix}/${encodeURIComponent(citySlug!)}`}
                                  className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
                                  onClick={() => {
                                    AnalyticsManager.getInstance().trackEvent('city_link_click', {
                                      restaurant_id: selectedEntry.id,
                                      city_name: city,
                                      event_category: 'navigation',
                                    });
                                  }}
                                >
                                  {city} →
                                </a>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="text-sm text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1 flex-wrap text-left">
                      {selectedEntry.inspectionFrom ? (
                        <>
                          <span>
                            {t('restaurant.lastInspection')}: {new Date(selectedEntry.inspectionFrom).toLocaleDateString()}
                          </span>
                          <span className="text-xs">
                            ({DateFormatter.formatRelativeTime(new Date(selectedEntry.inspectionFrom))})
                          </span>
                          {(() => {
                            const inspDate = new Date(selectedEntry.inspectionFrom);
                            const now = new Date();
                            const monthsDiff = (now.getFullYear() - inspDate.getFullYear()) * 12 + (now.getMonth() - inspDate.getMonth());
                            if (monthsDiff > 36) {
                              return (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  {t('restaurant.scoreOutdated', 'Score may no longer reflect current state')}
                                </span>
                              );
                            }
                            if (monthsDiff > 18) {
                              return (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">
                                  {t('restaurant.possiblyOutdated', 'Possibly outdated')}
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </>
                      ) : (
                        <span>{t('restaurant.lastInspection')}: N/A</span>
                      )}
                      <button
                        onClick={() => {
                          AnalyticsManager.getInstance().trackEvent('inspection_source_click', {
                            restaurant_id: selectedEntry.id,
                            restaurant_name: selectedEntry.name,
                            event_category: 'engagement',
                          });
                          openModal('inspectionSource');
                        }}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors ml-0.5"
                        aria-label={t('restaurant.inspectionSource.info')}
                      >
                        <InfoIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Tier 3 — Actions */}
                  <div className="mt-3 relative">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          AnalyticsManager.getInstance().trackEvent('subscribe_click', {
                            restaurant_id: selectedEntry.id,
                            restaurant_name: selectedEntry.name,
                            event_category: 'engagement',
                          });
                          setShowSubscription(true);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 text-sm font-medium"
                        aria-label={t('subscription.subscribe', 'Subscribe')}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        <span>{t('subscription.subscribe', 'Subscribe')}</span>
                      </button>
                      <button
                        onClick={handleShareClick}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 text-sm font-medium"
                        aria-label={t('restaurant.share')}
                      >
                        <ShareIcon className="h-4 w-4" />
                        <span>{t('restaurant.share')}</span>
                      </button>
                    </div>
                    {/* Inline share buttons for desktop / Web Share API fallback */}
                    {showShareButtons && (() => {
                      const data = getShareData();
                      if (!data) return null;
                      const fullText = `${data.shareText} ${data.shareUrl}`;
                      return (
                        <div className="flex gap-2 mt-2 animate-fade-in">
                          <a
                            href={`https://wa.me/?text=${encodeURIComponent(fullText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              AnalyticsManager.getInstance().trackEvent('share_platform', {
                                platform: 'whatsapp',
                                restaurant_id: selectedEntry.id,
                                restaurant_name: selectedEntry.name,
                                event_category: 'sharing',
                              });
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            WhatsApp
                          </a>
                          <a
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(data.shareText)}&url=${encodeURIComponent(data.shareUrl)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              AnalyticsManager.getInstance().trackEvent('share_platform', {
                                platform: 'twitter',
                                restaurant_id: selectedEntry.id,
                                restaurant_name: selectedEntry.name,
                                event_category: 'sharing',
                              });
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            X
                          </a>
                          <button
                            onClick={() => {
                              AnalyticsManager.getInstance().trackEvent('share_platform', {
                                platform: 'clipboard',
                                restaurant_id: selectedEntry.id,
                                restaurant_name: selectedEntry.name,
                                event_category: 'sharing',
                              });
                              copyToClipboard(data.shareUrl);
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 text-sm font-medium rounded-lg transition-colors"
                          >
                            {copied ? t('restaurant.copied') : t('restaurant.copyLink', 'Copy link')}
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                </div>

                {/* Collapsible Details & History */}
                <details
                  className="mt-2 smooth-transition"
                  open={isDetailsHistoryOpen}
                  onToggle={handleDetailsHistoryToggle}
                >
                  <summary className={styles.detailsSummary}>
                    <span className={styles.detailsSummaryLabel}>
                      <svg className={styles.detailsChevron} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {t('restaurant.detailsAndHistory', 'Details & History')}
                    </span>
                    <span className={styles.detailsAction}>
                      {isDetailsHistoryOpen
                        ? t('restaurant.collapseDetails', 'Hide details')
                        : t('restaurant.expandDetails', 'Show details')}
                    </span>
                  </summary>
                  <div className="pt-1 pb-2">
                    <span
                      onClick={() => {
                        AnalyticsManager.getInstance().trackEvent('badge_trigger_click', {
                          restaurant_id: selectedEntry.id,
                          restaurant_name: selectedEntry.name,
                          event_category: 'engagement',
                        });
                        openModal('badge');
                      }}
                      className="text-[11px] text-gray-500 hover:text-gray-400 transition-colors cursor-pointer block mb-2"
                    >
                      {t('badge.triggerLink')}
                    </span>
                    {/* Comparison Badges (city rank, "Waarom deze score?") */}
                    <ComparisonBadges
                      comparison={comparison}
                      isLoading={isLoadingComparison}
                    />

                    {/* Chain membership */}
                    {chain && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 mt-2" data-testid="chain-section">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          {t('chain.sectionTitle', 'Part of a chain?')}
                        </h4>
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                          {t('chain.sectionBody', '{{count}} locations share this name in our database.', { count: chain.locationCount })}
                        </p>
                        {chain.avgScore != null && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {t('chain.sectionAvg', 'Average FAVV score across all locations: {{avg}}/5', { avg: chain.avgScore.toFixed(1) })}
                          </p>
                        )}
                        <a
                          href={`/${i18n.language || 'en'}/${getChainPath(i18n.language || 'en')}/${chain.slug}`}
                          onClick={(e) => {
                            e.preventDefault();
                            AnalyticsManager.getInstance().trackEvent('chain_section_click', {
                              restaurant_id: selectedEntry?.id,
                              chain_slug: chain.slug,
                              event_category: 'navigation',
                            });
                            // Unregister the sidebar overlay before the synthetic
                            // popstate or NavigationManager swallows it as "back"
                            sidebarStateManager.closeSidebar();
                            const lang = i18n.language || 'en';
                            window.history.pushState({}, '', `/${lang}/${getChainPath(lang)}/${chain.slug}`);
                            window.dispatchEvent(new PopStateEvent('popstate'));
                          }}
                          className="inline-block mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 no-underline"
                        >
                          {t('chain.sectionLink', 'View all locations')} →
                        </a>
                      </div>
                    )}

                    {/* History Chart */}
                    {isLoadingHistory ? (
                      <div className={`${styles.card} ${styles.historyCard} mt-2`}>
                        <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-1">{t('restaurant.inspectionHistory')}</h4>
                        <SkeletonGroup>
                          <Skeleton className="h-24 w-full mt-2" />
                          <div className="flex justify-between mt-2">
                            <Skeleton className="h-3 w-16" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                        </SkeletonGroup>
                      </div>
                    ) : historyError ? (
                      <div className={`${styles.card} ${styles.historyCard} mt-2`}>
                        <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-1">{t('restaurant.inspectionHistory')}</h4>
                        <div className="text-red-400 text-sm">{historyError}</div>
                      </div>
                    ) : (history && history.entries.length > 0) || reports.length > 0 ? (
                      <div className={`${styles.card} ${styles.historyCard} mt-2`}>
                        <h4 className="font-medium text-gray-700 dark:text-gray-200 mb-1">{t('restaurant.inspectionHistory')}</h4>
                        <CombinedRatingsTimeline
                          historyData={history ?? undefined}
                          userReports={reports}
                          onReportClick={scrollToReport}
                          subscriberCount={subscriberCount}
                          isLoadingSubscriberCount={isLoadingSubscriberCount}
                          subscriberCountError={subscriberCountError}
                        />
                      </div>
                    ) : null}
                  </div>
                </details>
              </>
            ) : (
              <>
                {/* Loading state when selectedEntry is null but sidebar should be shown */}
                <div className={`${styles.card} ${styles.restaurantCard} smooth-transition fade-in`}>
                  <div className={styles.restaurantHeader}>
                    <SkeletonGroup>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <Skeleton className="h-6 w-48" />
                      </div>
                      <SkeletonText lines={2} className="mt-3" />
                      <div className="flex gap-2 mt-4">
                        <Skeleton className="h-9 w-24 rounded-lg" />
                        <Skeleton className="h-9 w-24 rounded-lg" />
                      </div>
                    </SkeletonGroup>
                  </div>
                </div>
              </>
            )}

            {/* Reports Section */}
            <div className="mt-3 pt-3 pb-4" id="reports-section">
              {/* Reports header with inline Add button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t('restaurant.userReports', 'Reports')}</h4>
                  {reports.length > 0 && (
                    <span className="text-[10px] text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 rounded-full px-2 py-0.5 font-medium">
                      {reports.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    const analytics = AnalyticsManager.getInstance();
                    analytics.trackConversion('report_started');
                    openModal('reportForm');
                  }}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors"
                >
                  <AddReportIcon />
                  {t('restaurant.addReport', 'Add report')}
                </button>
              </div>
              {/* Report Form Modal — mounted only while open (code-split) */}
              {modals.reportForm && (
              <Suspense fallback={null}>
              <ReportFormModal
                isOpen={modals.reportForm}
                onClose={() => {
                  closeModal('reportForm');
                  setSubmitError(null);
                  // If the form was successfully submitted, reset the success state after closing
                  if (submitSuccess) {
                    setTimeout(() => {
                      setSubmitSuccess(false);
                    }, 300);
                  }
                }}
                restaurantId={selectedEntry ? Number(selectedEntry.id) || 0 : Number(selectedRestaurantId) || 0}
                restaurantName={selectedEntry?.name}
                isSubmitting={isSubmitting}
                submitSuccess={submitSuccess}
                submitError={submitError}
                onSubmit={async (formData, imageFile) => {
                  // Clear previous states
                  setIsSubmitting(true);
                  setSubmitError(null);

                  try {
                    // Call the API service
                    const result = await restoApiService.submitRestaurantReport(formData as ReportRequest, imageFile);

                    if (result.success) {
                      setSubmitSuccess(true);
                      // Refresh the reports list
                      const restaurantId = selectedEntry
                        ? Number(selectedEntry.id) || 0
                        : Number(selectedRestaurantId) || 0;
                      fetchReports(restaurantId);
                      // Keep the success view open so the user can use the
                      // share buttons; it closes via its Done button/backdrop.
                    } else {
                      setSubmitError(result.error || "Failed to submit report. Please try again.");
                    }
                  } catch (error) {
                    setSubmitError(
                      error instanceof Error
                        ? error.message
                        : "An unexpected error occurred. Please try again.",
                    );
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
              />
              </Suspense>
              )}

              {/* Reports List */}
              <div className="smooth-transition fade-in">
                {isLoadingReports ? (
                  <SkeletonGroup className="space-y-3">
                    <SkeletonReportCard />
                    <SkeletonReportCard />
                  </SkeletonGroup>
                ) : reportsError ? (
                  <div
                    className={`${styles.card} p-4 bg-red-900/50 border border-red-700 rounded-md text-red-300 text-sm`}
                  >
                    <p className="flex items-center">
                      <ErrorIcon />
                      {reportsError}
                    </p>
                    <button
                      onClick={() => {
                        const restaurantId = selectedEntry 
                          ? Number(selectedEntry.id) || 0 
                          : Number(selectedRestaurantId) || 0;
                        fetchReports(restaurantId);
                      }}
                      className="mt-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                    >
                      {t('restaurant.tryAgain')}
                    </button>
                  </div>
                ) : reports.length === 0 ? (
                  <div className={`${styles.card} text-center py-6 text-gray-500 dark:text-gray-400`}>
                    <EmptyStateIcon />
                    <p>{t('restaurant.noReportsYet')}</p>
                    <p className="text-sm mt-1">{t('restaurant.beFirstToReport')}</p>
                  </div>
                ) : (
                  <div className="smooth-transition fade-in space-y-3">
                    {reports.map((report) => (
                      <ReportListItem
                        key={report.id}
                        id={report.id}
                        username={report.username}
                        date={report.date}
                        comment={report.comment}
                        rating={report.rating}
                        avatarColor={report.avatarColor}
                        imageUrl={report.imageUrl}
                        confirmations={report.confirmations}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Big CTA at bottom of reports */}
              <button
                onClick={() => {
                  const analytics = AnalyticsManager.getInstance();
                  analytics.trackConversion('report_started');
                  openModal('reportForm');
                }}
                className="w-full mt-4 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md flex items-center justify-center gap-2 ripple font-medium"
              >
                <AddReportIcon />
                {t('restaurant.addReport')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RestaurantDetailsContent;
